import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import DefaultLayout from '~/pages/_layouts/full';
import Box from '~/components/Box';
import Button from '~/components/Button';
import PageTitle from '~/components/PageTitle';
import Pagination from '~/components/Pagination';
import { Table, TableActions } from '~/components/Table';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, Popconfirm } from 'antd';
import { BreadcrumbCustom } from '~/styles/global';
import { countUnReadNotifications, deleteNotification, paginationNotifications, updateViewedNotification } from '~/services/hooks/notificationUsers';
import Filter from './filter';
import { DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';
import { updateNotifications } from '~/store/modules/user/actions';

export default function Notifications() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({});
  const [tableData, setTableData] = useState([]);
  const [sortBy, setSortBy] = useState('date');
  const [sortDirection, setSortDirection] = useState('DESC');
  const [filters, setFilters] = useState([]);

  const fetchData = async page => {
    setLoading(true);
    const data = await paginationNotifications({
      page: page - 1,
      sortBy,
      sortDirection,
      filters: filters,
    });

    setTableData(data.content);

    const metaData = {
      first: data.first,
      last: data.last,
      numberOfElements: data.numberOfElements,
      pageNumber: data.pageable.pageNumber + 1,
      pageSize: data.pageable.pageSize,
      totalElements: data.totalElements,
    };
    setMeta(metaData);
    setLoading(false);
  };

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortBy]);

  const handleDelete = async id => {
    await deleteNotification(id);
    fetchData(1);
    countNotifications();
  };

  const handleViewed = async record => {
    if (record.viewed === null) {
      await updateViewedNotification(record);
      fetchData(1);
      countNotifications();
    }
  };

  const countNotifications = async () => {
    const data = await countUnReadNotifications();
    dispatch(updateNotifications(data));
  };

  const style = {
    breadcrumb: { margin: '16px 0' },
    pageTitle: { marginRight: 'auto', width: '100%' },
    btnNew: {
      col: { marginLeft: 'auto', paddingBottom: '20px' },
      btn: { marginLeft: 'auto' },
    },
    title: { fontWeight: 'bold' },
    subtitle: { fontSize: '12px' },
    row: { fontWeight: 'normal' }
  };

  const tableColumns = [
    {
      title: t('screens:notificationUser.message'),
      key: 'message',
      align: 'left',
      sorter: true,
      ellipsis: {
        showTitle: true,
      },
      render: record => (
        <>
          <span style={record.viewed === null ? style.title : style.row}>{record.sender.name}</span>
          <br />
          <span style={style.subtitle}>{record.type === 'LIEN_WAIVER' ? <a href="/notary-dashboard">{record.message}</a> : record.message}</span>
        </>
      ),
    },
    {
      title: t('screens:notificationUser.date'),
      key: 'date',
      align: 'center',
      width: 100,
      render: record => (
        <span style={record.viewed === null ? style.title : style.row}>{
          record.date ?
            moment(record.date).format('MM/DD/YY') === moment(new Date()).format('MM/DD/YY')
              ? moment(record.date).format('HH:mm')
              : moment(record.date).format('MM/DD/YY')
            : 'N/A'
        }</span>
      ),
    },
    {
      title: t('screens:notificationUser.actions'),
      key: 'actions',
      align: 'center',
      ellipsis: {
        showTitle: true,
      },
      className: 'table-action',
      width: 85,
      render: record => (
        <TableActions>
          <Popconfirm
            icon={false}
            title={t('messages:confirmDelete')}
            onConfirm={(e) => handleDelete(record.id)}
            okText={t('messages:yes')}
            cancelText={t('messages:no')}
          >
            <Button title={t('messages:delete')}>
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        </TableActions>
      ),
    },
  ];

  const breadcrumb =
    <BreadcrumbCustom>
      <Breadcrumb.Item href="/">{t('menus:home')}</Breadcrumb.Item>
      <Breadcrumb.Item>{t('menus:notifications')}</Breadcrumb.Item>
    </BreadcrumbCustom>
    ;

  const handleTableChange = (sorter) => {
    const sortOrder = sorter.order === 'ascend' ? '' : 'DESC';
    if (sorter.field !== sortBy || sortOrder !== sortDirection) {
      setSortBy(sorter.field);
      setSortDirection(sortOrder);
      fetchData(1);
    }
  };

  return (
    <DefaultLayout breadcrumb={breadcrumb}>
      <PageTitle title={t('menus:notifications')} />
      <Filter isLoading={loading} setFilters={setFilters} />
      <Box>
        <Table
          bordered
          rowKey="id"
          onRow={(record) => ({
            style: { background: record.viewed === null ? 'default' : '#c2dbff' },
            onClick: () => {
              handleViewed(record);
            }
          })}
          pagination={false}
          loading={loading}
          columns={tableColumns}
          dataSource={tableData}
          onChange={handleTableChange}
        />
        <Pagination
          onChange={value => {
            fetchData(value);
          }}
          meta={meta}
        />
      </Box>
    </DefaultLayout>
  );
}
